import { useEffect } from "react";
import { Clock, Check, ChefHat } from "lucide-react";
import { toast } from "sonner";
import { useActiveOrders, useUpdateOrderStatus } from "@/features/orders/api";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui";
import { PageLoader, PageError, EmptyState } from "@/components/common";
import { cn } from "@/lib/utils";
import type { Order } from "@/types";

export function KitchenDisplayPage() {
  const { data: ordersData, isLoading, error, refetch } = useActiveOrders();
  const updateStatus = useUpdateOrderStatus();

  const orders = ordersData?.data ?? [];

  useEffect(() => {
    const interval = setInterval(() => refetch(), 30000);

    return () => clearInterval(interval);
  }, [refetch]);

  const handleStatusUpdate = async (
    orderId: string,
    status: Order["status"],
  ) => {
    try {
      await updateStatus.mutateAsync({ id: orderId, data: { status } });
      toast.success(`Order marked as ${status}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update order",
      );
    }
  };

  if (isLoading) return <PageLoader />;
  if (error)

    return <PageError message="Failed to load orders" onRetry={refetch} />;

  const pendingOrders = orders.filter((o) => o.status === "pending");
  const preparingOrders = orders.filter((o) => o.status === "preparing");

  return (
    <div className="min-h-screen bg-muted p-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <ChefHat className="h-8 w-8" />
          Kitchen Display
        </h1>
        <Badge variant="outline" className="text-lg">
          {orders.length} Active Orders
        </Badge>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-amber-600">
            <Clock className="h-5 w-5" />
            New Orders ({pendingOrders.length})
          </h2>
          <div className="space-y-3">
            {pendingOrders.length === 0 ? (
              <EmptyState
                icon={Clock}
                title="No new orders"
                description="New orders will appear here"
              />
            ) : (
              pendingOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onStartPreparing={() =>
                    handleStatusUpdate(order.id, "preparing")
                  }
                  variant="pending"
                />
              ))
            )}
          </div>
        </div>
        <div>
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-blue-600">
            <ChefHat className="h-5 w-5" />
            Preparing ({preparingOrders.length})
          </h2>
          <div className="space-y-3">
            {preparingOrders.length === 0 ? (
              <EmptyState
                icon={ChefHat}
                title="No orders preparing"
                description="Start preparing an order"
              />
            ) : (
              preparingOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onComplete={() => handleStatusUpdate(order.id, "ready")}
                  variant="preparing"
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface OrderCardProps {
  order: Order;
  onStartPreparing?: () => void;
  onComplete?: () => void;
  variant: "pending" | "preparing";
}

function OrderCard({
  order,
  onStartPreparing,
  onComplete,
  variant,
}: OrderCardProps) {
  const elapsedMinutes = Math.floor(
    (Date.now() - new Date(order.createdAt).getTime()) / 60000,
  );

  return (
    <Card
      className={cn(
        "transition-all hover:shadow-lg",
        variant === "pending" && "border-amber-200 bg-amber-50",
        variant === "preparing" && "border-blue-200 bg-blue-50",
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold">
            Order #{order.orderNumber}
          </CardTitle>
          <Badge
            variant={elapsedMinutes > 10 ? "destructive" : "secondary"}
            className="gap-1"
          >
            <Clock className="h-3 w-3" />
            {elapsedMinutes}m
          </Badge>
        </div>
        {order.customer && (
          <p className="text-sm text-muted-foreground">{order.customer.name}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="mb-4 space-y-2">
          {order.items?.map((item, idx) => (
            <div
              key={idx}
              className="flex items-start justify-between rounded bg-white/50 p-2"
            >
              <div>
                <p className="font-medium">
                  {item.quantity}x {item.productName}
                </p>
                {item.variantName && item.variantName !== "Default" && (
                  <p className="text-sm text-muted-foreground">
                    {item.variantName}
                  </p>
                )}
                {item.modifiers && item.modifiers.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    + {item.modifiers.map((m) => m.name).join(", ")}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        {order.notes && (
          <div className="mb-4 rounded bg-yellow-100 p-2 text-sm">
            <strong>Note:</strong> {order.notes}
          </div>
        )}

        {variant === "pending" && onStartPreparing && (
          <Button onClick={onStartPreparing} className="w-full">
            <ChefHat className="mr-2 h-4 w-4" />
            Start Preparing
          </Button>
        )}

        {variant === "preparing" && onComplete && (
          <Button
            onClick={onComplete}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            <Check className="mr-2 h-4 w-4" />
            Mark Ready
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
